import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * THE GREAT LIBRARY (Большая библиотека) — the hushed, scholarly heart of Castle Hearthhold.
 *
 * Composition: tall arched BOOKCASES line the walls in a calm, evenly-spaced rhythm (varied heights,
 * no two alike), framing one clear HERO at the room's interior — a candle-lit READING ISLAND (a table
 * under a hung chandelier, a chair facing across each long side) on a quiet runner. A standing LECTERN
 * with an open tome gives the scholar's focal beat just south of it, and a single window armchair reads
 * by the SW corner. Everything hugs the walls or the reading island; the central cross and door mouths
 * stay open, and the floor is allowed to breathe — no scattered junk, one warm light pool per zone.
 *
 * Geometry: zone (2800,360) 440×400 → centre (3020,560). Doors on the N wall (x3020), the W wall (y560),
 * the E wall (y560) and the S wall (x2990) are kept clear.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;
  const cx = 3020;
  const cy = 560;

  // ── Floor: a quiet runner laid first, drawing the reading axis (under everything). ──
  place(ctx, kit.runnerRug(art, 240, 90), cx, cy);

  // ── BOOKCASES lining the walls — an even, calm rhythm; varied heights, never two alike. ──
  place(ctx, kit.bookcase(art, 150), 2850, 470); // NW — tallest, by the W wall
  place(ctx, kit.bookcase(art, 130), 3020, 440); // N wall (clear of the N door mouth column)
  place(ctx, kit.bookcase(art, 140), 3190, 470); // NE
  place(ctx, kit.bookcase(art, 145), 2850, 650); // SW stack
  place(ctx, kit.bookcase(art, 135), 3190, 650); // SE stack
  place(ctx, kit.scrollShelf(art, 70, 80), 2920, 440); // a single scroll-cubby tucked on the N wall

  // ── HERO: the interior READING ISLAND — a candle-lit table, one chair facing across each long side. ──
  place(ctx, kit.smallTable(art, 34), 3140, 565);
  place(ctx, kit.woodChair(art, Math.PI / 2), 3140, 480); // north chair (seat library-read-1) — faces down
  place(ctx, kit.woodChair(art, -Math.PI / 2), 3140, 650); // south chair (seat library-read-2) — faces up

  // ── A window READING ARMCHAIR in the SW corner, looking out (seat library-armchair). ──
  place(ctx, kit.woodChair(art, -Math.PI / 2), 2880, 680);

  // ── A standing LECTERN with an open tome — the scholar's focal beat at the room's interior. ──
  place(ctx, kit.lectern(art), 3020, 640);

  // ── ONE warm light pool: a chandelier hung high dead-centre over the reading island. ──
  place(ctx, kit.chandelier(art), cx, cy);

  // ── Wall warmth: a torch on each long wall + a single scholarly-blue heraldic banner. ──
  place(ctx, kit.wallTorch(art), 2810, 470);
  place(ctx, kit.wallTorch(art), 3230, 470);
  place(ctx, kit.banner(art, 0x2f4a8a, 90), 3230, 650); // cool scholarly blue, hugging the E wall
};

export default decorate;
